import { applePayButtonTypes } from './types';
import type { ApplePayButtonTypes } from './types';
import type { ApplePayButtonType } from '@trx/shared';

export const DEFAULT_APPLE_PAY_BUTTON_TYPE = 'plain';

const appleButtonTypeValues: Record<ApplePayButtonTypes, string> = {
  [applePayButtonTypes.PLAIN]: 'plain',
  [applePayButtonTypes.BUY]: 'buy',
  [applePayButtonTypes.ADD_MONEY]: 'add-money',
  [applePayButtonTypes.BOOK]: 'book',
  [applePayButtonTypes.CHECKOUT]: 'check-out',
  [applePayButtonTypes.CONTINUE]: 'continue',
  [applePayButtonTypes.CONTRIBUTE]: 'contribute',
  [applePayButtonTypes.DONATE]: 'donate',
  [applePayButtonTypes.IN_STORE]: 'pay',
  [applePayButtonTypes.ORDER]: 'order',
  [applePayButtonTypes.RELOAD]: 'reload',
  [applePayButtonTypes.RENT]: 'rent',
  [applePayButtonTypes.SET_UP]: 'set-up',
  [applePayButtonTypes.SUBSCRIBE]: 'subscribe',
  [applePayButtonTypes.SUPPORT]: 'support',
  [applePayButtonTypes.TIP]: 'tip',
  [applePayButtonTypes.TOP_UP]: 'top-up',
};

/**
 * Maps the button type accepted by the field to the `-apple-pay-button-type` value.
 * The cast covers 'continue' and 'pay', which the shared button type list is missing.
 */
export const toAppleButtonType = (buttonType: ApplePayButtonTypes): ApplePayButtonType =>
  appleButtonTypeValues[buttonType] as ApplePayButtonType;

export const isValidApplePayButtonType = (
  applePayButtonTypeArg: string | null | undefined,
): applePayButtonTypeArg is ApplePayButtonTypes => {
  if (!applePayButtonTypeArg) {
    return false;
  }

  return Object.values(applePayButtonTypes).includes(applePayButtonTypeArg as ApplePayButtonTypes);
};

export const BUSINESS_NAME_MAX_LENGTH = 50;

const isDisplayableChar = (char: string): boolean => {
  const code = char.charCodeAt(0);

  // Control characters
  if (code <= 0x1f || code === 0x7f) {
    return false;
  }

  // Zero-width characters
  if (code >= 0x200b && code <= 0x200f) {
    return false;
  }

  // Bidirectional formatting characters, they can be used to spoof the displayed value
  if (code >= 0x202a && code <= 0x202e) {
    return false;
  }

  // Byte order mark
  if (code === 0xfeff) {
    return false;
  }

  return true;
};

/**
 * The business name comes from a query param, so it can't be trusted even though
 * the library validates it - strips the characters which are not displayable
 * on the Apple Pay popup and cuts the value to the maximum allowed length.
 */
export const sanitizeBusinessName = (businessName: string | null): string | null => {
  if (!businessName) {
    return null;
  }

  const sanitizedBusinessName = Array.from(businessName).filter(isDisplayableChar).join('').trim();

  return sanitizedBusinessName ? sanitizedBusinessName.slice(0, BUSINESS_NAME_MAX_LENGTH) : null;
};

export const applePayBtnLimits = {
  borderRadius: () => ({
    defaultValue: 4,
    min: 0,
    max: 100,
  }),
  height: (buttonType: ApplePayButtonTypes) => {
    switch (buttonType) {
      case applePayButtonTypes.PLAIN:
        return {
          defaultValue: undefined,
          min: 40,
          max: 100,
        };

      default:
        return {
          defaultValue: undefined,
          min: 30,
          max: 100,
        };
    }
  },
  width: (buttonType: ApplePayButtonTypes) => {
    switch (buttonType) {
      case applePayButtonTypes.PLAIN:
        return {
          defaultValue: undefined,
          min: 160,
          max: 800,
        };

      default:
        return {
          defaultValue: undefined,
          min: 100,
          max: 800,
        };
    }
  },
};

export const clamp = (
  value: string | null,
  limits: {
    min: number;
    max: number;
    defaultValue: number | undefined;
  },
): number | undefined => {
  return !value || isNaN(Number(value)) ? limits.defaultValue : Math.min(Math.max(Number(value), limits.min), limits.max);
};
